import React from 'react';
import styled from 'styled-components';

import { type PlatformSettingsFields } from '../index';
import { RenderFields, type SectionWithFields } from '~/components/RenderFields';
import { Section } from '~/components/Section';
import { SectionHeader } from '~/components/SectionHeader';
import { TransferApprovalConfiguration } from '~/lib/transfers/components/TransferApprovalConfiguration';
import { Card, Flex, Inner } from '~/styles';

import { AdminApprovalConfiguration } from './AdminApprovalConfiguration';

const RuleDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  font-size: 12px;
  line-height: 18px;
`;

const RuleFields = styled.div`
  .form-field:has(#user-change-approval-control) > div:first-child {
    display: flex;
    align-items: center;
    min-height: 24px;

    > div:first-child {
      display: flex;
      align-items: center;
      margin-top: 0;
    }
  }
`;

interface Props {
  canConfigureTransferApprovals: boolean;
}

export const ApprovalRules: React.FC<Props> = ({ canConfigureTransferApprovals }) => {
  const sections = [
    {
      headline: 'Transfers',
      variant: 'isCompact',
      description: `Configures transfer types which require approval. Transfers may be approved by users with the “Approve Transfers” permission on their role.${
        !canConfigureTransferApprovals
          ? ' Contact Column support to configure transfer approvals for this platform.'
          : ''
      }`,
      fields: [{ content: <TransferApprovalConfiguration canConfigure={canConfigureTransferApprovals} /> }],
    },
    {
      headline: 'Platform Administration',
      variant: 'isCompact',
      description:
        'Configures whether administrative changes require approval. Changes may be approved by admins and users with the "User Management" permission on their role.',
      fields: [
        {
          id: 'requireUserChangeApproval',
          label: 'User Access Changes',
          tooltip: 'Requires approval for team invites, user role changes, and changes to custom role permissions.',
          children: ({ value, onChange }) => <AdminApprovalConfiguration value={value === true} onChange={onChange} />,
        },
      ],
    },
  ] satisfies SectionWithFields<PlatformSettingsFields, undefined>[];

  return (
    <Section headline="Approval Rules">
      <Flex flexDirection="column" gap={16}>
        {sections.map(({ headline, description, fields }) => (
          <Card key={headline} variant="secondary">
            <SectionHeader text={headline} className="!pb-1 [&_h2]:text-sm" />
            <Inner px="var(--page-margin)" pt={0} pb={16}>
              <Inner p={0} pb={20}>
                <RuleDescription>{description}</RuleDescription>
              </Inner>
              <RuleFields>
                <RenderFields<PlatformSettingsFields> sections={[{ variant: 'isCompact', fields }]} />
              </RuleFields>
            </Inner>
          </Card>
        ))}
      </Flex>
    </Section>
  );
};
